import { useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { CssBaseline, ThemeProvider as MuiThemeProvider } from '@mui/material';
import type { PaletteMode } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { deDE, enUS, hiIN, jaJP, ruRU, trTR, zhCN } from '@mui/material/locale';

import { useLocale } from '@/shared/i18n';
import type { SupportedLocale } from '@/shared/i18n';
import { ThemeModeContext } from '@/shared/theme/ThemeModeContext';
import { createObzrTheme } from '@/shared/theme/theme';

const MUI_LOCALE_MAP: Record<SupportedLocale, object> = {
  en: enUS,
  ru: ruRU,
  'zh-CN': zhCN,
  'zh-TW': zhCN, // zh-TW not in MUI v7; use zhCN as fallback
  de: deDE,
  tr: trTR,
  hi: hiIN,
  ja: jaJP,
};

const STORAGE_KEY = 'obzr-merchant-theme-mode';

function readStoredMode(): PaletteMode {
  if (typeof window === 'undefined') {
    return 'light';
  }
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function persistMode(mode: PaletteMode): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* storage unavailable (private mode / SSR) — keep the in-memory mode */
  }
}

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Applies the OBZR design-system MUI theme with a persisted light/dark mode,
 * a normalising CSS baseline, and a {@link ThemeModeContext} for the app-bar toggle.
 */
export function ThemeProvider({ children }: ThemeProviderProps) {
  const [mode, setModeState] = useState<PaletteMode>(readStoredMode);
  const { locale } = useLocale();

  const setMode = useCallback((next: PaletteMode) => {
    setModeState(next);
    persistMode(next);
  }, []);

  const toggleMode = useCallback(() => {
    setModeState((prev) => {
      const next: PaletteMode = prev === 'dark' ? 'light' : 'dark';
      persistMode(next);
      return next;
    });
  }, []);

  const theme = useMemo(
    () => createTheme(createObzrTheme(mode), MUI_LOCALE_MAP[locale] ?? enUS),
    [mode, locale],
  );
  const contextValue = useMemo(() => ({ mode, toggleMode, setMode }), [mode, toggleMode, setMode]);

  return (
    <ThemeModeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeModeContext.Provider>
  );
}
